import type { ConditionGroup } from "@capture/shared/types";
import type { Behavior, Stage } from "@capture/shared";
import { ConditionGroupEditor } from "../../components/ConditionGroup";
import type { FieldDefinitionSummary } from "../../hooks/useJobTypeFields";
import styles from './export-composer.module.css';

/**
 * Minimal field-summary shape needed by the routing condition editor.
 * Carrying `dataType` end-to-end is what makes ConditionRow expose the
 * right operator set (numeric vs text vs date vs boolean) — see #60.
 *
 * `stageMap` is optional and only consumed by sample-data preview
 * (#53) — RoutingPanel ignores it. When supplied, the export preview
 * can evaluate compute@<stage> static behaviors instead of falling
 * back to placeholder data.
 */
export interface RoutingFieldSummary {
  name: string;
  label: string;
  dataType: string;
  stageMap?: Partial<Record<Stage, Behavior[]>>;
}

interface Props {
  condition: ConditionGroup | undefined;
  onChange: (condition: ConditionGroup | undefined) => void;
  fields: RoutingFieldSummary[];
}

const SPECIAL_FIELDS: FieldDefinitionSummary[] = [
  { id: "_classification", name: "_classification", label: "Classification", dataType: "text", groupId: null, groupName: "System" },
  { id: "_batchName", name: "_batchName", label: "Batch Name", dataType: "text", groupId: null, groupName: "System" },
  { id: "_priority", name: "_priority", label: "Priority", dataType: "number", groupId: null, groupName: "System" },
  { id: "_pageCount", name: "_pageCount", label: "Page Count", dataType: "number", groupId: null, groupName: "System" },
  { id: "_operatorName", name: "_operatorName", label: "Operator Name", dataType: "text", groupId: null, groupName: "System" },
];

function buildFieldSummaries(fields: RoutingFieldSummary[]): FieldDefinitionSummary[] {
  const userFields: FieldDefinitionSummary[] = fields.map((f) => ({
    id: f.name,
    name: f.name,
    label: f.label || f.name,
    dataType: f.dataType,
    groupId: null,
    groupName: "Fields",
  }));
  return [...userFields, ...SPECIAL_FIELDS];
}

function countConditions(group: ConditionGroup): number {
  let count = 0;
  for (const cond of group.conditions) {
    if ("logic" in cond) {
      count += countConditions(cond as ConditionGroup);
    } else {
      count += 1;
    }
  }
  return count;
}

export function RoutingPanel({ condition, onChange, fields }: Props) {
  const fieldSummaries = buildFieldSummaries(fields);

  function handleAddCondition() {
    const initial: ConditionGroup = {
      logic: "and",
      conditions: [
        {
          source: { type: "field", name: fields[0]?.name ?? "_classification" },
          operator: "equals",
          value: { type: "literal", value: "" },
        },
      ],
    };
    onChange(initial);
  }

  function handleClear() {
    onChange(undefined);
  }

  if (!condition) {
    return (
      <div className={`${styles.routingPanel} ${styles.routingPanelEmpty}`}>
        <p className={styles.routingHint}>
          All documents will be exported to this destination. Add a condition to filter.
        </p>
        <button
          type="button"
          className={styles.routingAddBtn}
          onClick={handleAddCondition}
        >
          Add Routing Condition
        </button>
      </div>
    );
  }

  const conditionCount = countConditions(condition);

  return (
    <div className={styles.routingPanel}>
      <div className={styles.routingHeader}>
        <button
          type="button"
          className={styles.routingClearBtn}
          onClick={handleClear}
        >
          Clear
        </button>
      </div>

      <ConditionGroupEditor
        group={condition}
        fields={fieldSummaries}
        onChange={onChange}
        compact={true}
      />

      <p className={styles.routingSummary}>
        {conditionCount} condition{conditionCount !== 1 ? "s" : ""} applied to this destination
      </p>
    </div>
  );
}
